import React, { useState, useCallback, useRef, useEffect } from 'react';
import { X } from 'lucide-react';

const MIN_SIZE = 40;

const ImageCropModal = ({ isOpen, onClose, imageSrc, onCropComplete, title = 'Crop Image' }) => {
  const [crop, setCrop] = useState(null); // { x, y, w, h } relative to container
  const [dragging, setDragging] = useState(null); // 'move' | 'nw' | 'ne' | 'sw' | 'se' | null
  const containerRef = useRef(null);
  const imageRef = useRef(null);
  const dragStart = useRef(null); // { px, py, snap }

  useEffect(() => {
    if (isOpen && imageSrc) {
      setCrop(null);
      setDragging(null);
      dragStart.current = null;
    }
  }, [isOpen, imageSrc]);

  const handleImageLoad = () => {
    if (!imageRef.current || !containerRef.current) return;
    const ir = imageRef.current.getBoundingClientRect();
    const cr = containerRef.current.getBoundingClientRect();
    const imgW = ir.width;
    const imgH = ir.height;
    const offX = ir.left - cr.left;
    const offY = ir.top - cr.top;
    const w = imgW * 0.8;
    const h = imgH * 0.8;
    setCrop({ x: offX + (imgW - w) / 2, y: offY + (imgH - h) / 2, w, h });
  };

  const getImageBounds = useCallback(() => {
    if (!imageRef.current || !containerRef.current) return null;
    const ir = imageRef.current.getBoundingClientRect();
    const cr = containerRef.current.getBoundingClientRect();
    return {
      left: ir.left - cr.left,
      top: ir.top - cr.top,
      right: ir.right - cr.left,
      bottom: ir.bottom - cr.top,
    };
  }, []);

  const startDrag = useCallback(
    (e, handle) => {
      e.preventDefault();
      e.stopPropagation();
      dragStart.current = { px: e.clientX, py: e.clientY, snap: { ...crop } };
      setDragging(handle);
    },
    [crop]
  );

  const handleMove = useCallback(
    e => {
      if (!dragging || !dragStart.current) return;
      e.preventDefault();
      const dx = e.clientX - dragStart.current.px;
      const dy = e.clientY - dragStart.current.py;
      const { snap } = dragStart.current;
      const b = getImageBounds();
      if (!b) return;

      setCrop(() => {
        let { x, y, w, h } = snap;
        if (dragging === 'move') {
          x = Math.max(b.left, Math.min(b.right - w, snap.x + dx));
          y = Math.max(b.top, Math.min(b.bottom - h, snap.y + dy));
        } else if (dragging === 'nw') {
          const nx = Math.max(b.left, Math.min(snap.x + dx, snap.x + snap.w - MIN_SIZE));
          const ny = Math.max(b.top, Math.min(snap.y + dy, snap.y + snap.h - MIN_SIZE));
          w = snap.x + snap.w - nx;
          h = snap.y + snap.h - ny;
          x = nx;
          y = ny;
        } else if (dragging === 'ne') {
          const ny = Math.max(b.top, Math.min(snap.y + dy, snap.y + snap.h - MIN_SIZE));
          w = Math.max(MIN_SIZE, Math.min(snap.w + dx, b.right - snap.x));
          h = snap.y + snap.h - ny;
          y = ny;
        } else if (dragging === 'sw') {
          const nx = Math.max(b.left, Math.min(snap.x + dx, snap.x + snap.w - MIN_SIZE));
          w = snap.x + snap.w - nx;
          x = nx;
          h = Math.max(MIN_SIZE, Math.min(snap.h + dy, b.bottom - snap.y));
        } else if (dragging === 'se') {
          w = Math.max(MIN_SIZE, Math.min(snap.w + dx, b.right - snap.x));
          h = Math.max(MIN_SIZE, Math.min(snap.h + dy, b.bottom - snap.y));
        }
        return { x, y, w, h };
      });
    },
    [dragging, getImageBounds]
  );

  const handleEnd = useCallback(() => {
    setDragging(null);
    dragStart.current = null;
  }, []);

  // Use pointer events — one unified path for mouse and touch, no dual-firing
  useEffect(() => {
    if (!dragging) return;
    document.addEventListener('pointermove', handleMove);
    document.addEventListener('pointerup', handleEnd);
    document.addEventListener('pointercancel', handleEnd);
    return () => {
      document.removeEventListener('pointermove', handleMove);
      document.removeEventListener('pointerup', handleEnd);
      document.removeEventListener('pointercancel', handleEnd);
    };
  }, [dragging, handleMove, handleEnd]);

  const handleSave = () => {
    if (!crop || !imageRef.current || !containerRef.current) return;
    const ir = imageRef.current.getBoundingClientRect();
    const cr = containerRef.current.getBoundingClientRect();
    const offX = ir.left - cr.left;
    const offY = ir.top - cr.top;
    const scaleX = imageRef.current.naturalWidth / ir.width;
    const scaleY = imageRef.current.naturalHeight / ir.height;
    const pixelCrop = {
      x: Math.round((crop.x - offX) * scaleX),
      y: Math.round((crop.y - offY) * scaleY),
      width: Math.round(crop.w * scaleX),
      height: Math.round(crop.h * scaleY),
    };
    if (onCropComplete) onCropComplete(pixelCrop, 0);
    onClose();
  };

  if (!isOpen || !imageSrc) return null;

  const Handle = ({ pos, cursor }) => (
    <div
      style={{
        position: 'absolute',
        width: 48,
        height: 48,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        top: pos.includes('n') ? -24 : undefined,
        bottom: pos.includes('s') ? -24 : undefined,
        left: pos.includes('w') ? -24 : undefined,
        right: pos.includes('e') ? -24 : undefined,
        cursor,
        touchAction: 'none',
        zIndex: 1,
      }}
      onPointerDown={e => {
        e.stopPropagation();
        startDrag(e, pos);
      }}
    >
      <div
        style={{
          width: 16,
          height: 16,
          background: '#ef4444',
          border: '2px solid white',
          borderRadius: 2,
          pointerEvents: 'none',
        }}
      />
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-2 sm:p-4 z-50">
      <div className="bg-black border border-gray-600 rounded-lg w-full max-w-4xl max-h-[95vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-gray-600 flex-shrink-0">
          <h3 className="text-lg font-bold text-white">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white p-1">
            <X size={24} />
          </button>
        </div>

        <div
          ref={containerRef}
          className="relative bg-gray-900 flex-1 min-h-0 overflow-hidden"
          style={{ minHeight: 320, userSelect: 'none', touchAction: 'none' }}
        >
          <img
            ref={imageRef}
            src={imageSrc}
            alt="Crop preview"
            className="max-w-full max-h-full object-contain mx-auto block h-full"
            onLoad={handleImageLoad}
            draggable={false}
          />

          {crop && (
            <div
              style={{
                position: 'absolute',
                left: crop.x,
                top: crop.y,
                width: crop.w,
                height: crop.h,
                border: '2px solid #ef4444',
                boxShadow: '0 0 0 9999px rgba(0,0,0,0.45)',
                cursor: 'move',
                touchAction: 'none',
              }}
              onPointerDown={e => startDrag(e, 'move')}
            >
              <div className="absolute inset-0 pointer-events-none">
                <div className="absolute left-1/3 top-0 bottom-0 w-px bg-red-500 opacity-40" />
                <div className="absolute left-2/3 top-0 bottom-0 w-px bg-red-500 opacity-40" />
                <div className="absolute top-1/3 left-0 right-0 h-px bg-red-500 opacity-40" />
                <div className="absolute top-2/3 left-0 right-0 h-px bg-red-500 opacity-40" />
              </div>
              <Handle pos="nw" cursor="nw-resize" />
              <Handle pos="ne" cursor="ne-resize" />
              <Handle pos="sw" cursor="sw-resize" />
              <Handle pos="se" cursor="se-resize" />
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 p-4 border-t border-gray-600 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!crop}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
          >
            Apply Crop
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImageCropModal;
